import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';
import {
    Container,
    Text,
    Heading,
    Button,
    Icon
} from '../components/ui/index.jsx';

export default function PrivacyPolicy() {
    const navigate = useNavigate();
    const lastUpdated = 'January 4, 2026';

    return (
        <div className="min-h-screen text-white selection:bg-[#7B61FF] selection:text-white relative overflow-hidden">
            {/* Aurora Background */}
            <div className="fixed inset-0 aurora-bg z-0 pointer-events-none"></div>

            {/* Ambient Orbs */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#7B61FF]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#9F7AEA]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Content Wrapper */}
            <div className="relative z-10 flex flex-col min-h-screen">
                <Navigation />

                <main className="flex-1">
                    <Container size="4xl" className="py-12">
                        {/* Header */}
                        <div className="text-center mb-12 px-4">
                            <Heading level={1} className="mb-4 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/50">
                                Privacy Policy
                            </Heading>
                            <Text variant="secondary" size="sm">
                                Last updated: {lastUpdated}
                            </Text>
                            <div className="w-24 h-1 bg-[#7B61FF] mx-auto rounded-full shadow-[0_0_10px_#7B61FF] mt-6"></div>
                        </div>

                        {/* Content */}
                        <div className="glass-panel p-8 md:p-12 rounded-2xl border-white/10 space-y-8">

                            {/* Introduction & Data Controller */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">1. Introduction & Data Controller</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    TheArchitect ("we," "our," or "us") is committed to protecting your privacy. This Privacy
                                    Policy explains how we collect, use, disclose, and safeguard your information when you use
                                    our AI-powered software architecture platform at theaiarchitect.ai (the "Service").
                                </Text>
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <Text variant="secondary" className="leading-relaxed">
                                        <strong className="text-white">Data Controller:</strong> TheArchitect is the data controller
                                        responsible for your personal data. This means we determine the purposes and means of processing
                                        your personal data and are accountable for its protection under applicable data protection laws
                                        including GDPR (EU), UK GDPR, CCPA/CPRA (California), and LGPD (Brazil).
                                    </Text>
                                </div>
                            </section>

                            {/* Information We Collect */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">2. Information We Collect</Heading>

                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Information from Google OAuth:</strong>
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
                                    <li>Email address</li>
                                    <li>Display name, first name, and last name</li>
                                    <li>Profile picture URL</li>
                                    <li>Language/locale preference</li>
                                    <li>Google Workspace domain (if applicable, for organizational accounts)</li>
                                    <li>Google account ID (for authentication only)</li>
                                </ul>

                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Information You Provide:</strong>
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
                                    <li>App ideas and descriptions you submit</li>
                                    <li>Answers to clarifying questions</li>
                                    <li>Feedback and support requests</li>
                                </ul>

                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Automatically Collected Information:</strong>
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300">
                                    <li>Session data and authentication tokens</li>
                                    <li>Usage data (features used, analysis sessions)</li>
                                    <li>Device and browser information</li>
                                    <li>Device fingerprint (a unique identifier generated from your browser and hardware
                                    characteristics, used solely for security purposes to detect unauthorized access and
                                    protect your account)</li>
                                    <li>IP address (hashed for security, not stored in plain text)</li>
                                </ul>
                            </section>

                            {/* How We Use Your Information */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">3. How We Use Your Information</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    We use your information for the following purposes:
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300">
                                    <li>To provide and maintain our Service</li>
                                    <li>To authenticate your identity and manage your account</li>
                                    <li>To process your app ideas through our AI system</li>
                                    <li>To track credit usage and manage subscriptions</li>
                                    <li>To send important service notifications</li>
                                    <li>To improve our Service and develop new features</li>
                                    <li>To respond to your inquiries and support requests</li>
                                    <li>To detect and prevent fraud or abuse</li>
                                </ul>
                            </section>

                            {/* Legal Basis for Processing */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">4. Legal Basis for Processing (GDPR)</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    Under GDPR and UK GDPR, we must have a lawful basis for processing your personal data:
                                </Text>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm text-gray-300">
                                        <thead>
                                            <tr className="border-b border-white/10">
                                                <th className="text-left py-2 text-white">Processing Activity</th>
                                                <th className="text-left py-2 text-white">Legal Basis</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/5">
                                            <tr><td className="py-2">Account creation & authentication</td><td className="py-2">Contract performance</td></tr>
                                            <tr><td className="py-2">Processing your app ideas via AI</td><td className="py-2">Contract performance</td></tr>
                                            <tr><td className="py-2">Payment processing</td><td className="py-2">Contract performance</td></tr>
                                            <tr><td className="py-2">Service notifications</td><td className="py-2">Legitimate interest</td></tr>
                                            <tr><td className="py-2">Fraud prevention & security</td><td className="py-2">Legitimate interest</td></tr>
                                            <tr><td className="py-2">Service improvement & analytics</td><td className="py-2">Legitimate interest</td></tr>
                                            <tr><td className="py-2">Marketing communications</td><td className="py-2">Consent (opt-in)</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            </section>

                            {/* AI Processing Disclosure */}
                            <section className="bg-[#7B61FF]/10 border border-[#7B61FF]/30 rounded-lg p-6">
                                <Heading level={3} className="mb-4 text-[#BA87FF]">5. AI Processing & Automated Decision-Making</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">EU AI Act Disclosure:</strong> You are interacting with an AI-powered
                                    system. When you submit content to our Service, it is processed using artificial intelligence
                                    provided by Anthropic (Claude API).
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
                                    <li>Your prompts are sent to Anthropic's API for processing</li>
                                    <li>Anthropic may retain data according to their privacy policy</li>
                                    <li>We do not sell your data to third parties</li>
                                    <li>AI outputs are generated based on your inputs and may contain errors</li>
                                </ul>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Automated Decision-Making (GDPR Art. 22):</strong> Our Service uses
                                    AI to generate software architecture recommendations. These outputs are suggestions only and do not
                                    constitute legally or financially binding decisions about you. You have the right to:
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
                                    <li>Request human review of any AI-generated output</li>
                                    <li>Express your point of view regarding AI outputs</li>
                                    <li>Contest decisions influenced by automated processing</li>
                                </ul>
                                <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                                    <Text variant="secondary" className="leading-relaxed">
                                        <strong className="text-green-400">No AI Training on Your Data:</strong> We do not use your
                                        input prompts, app ideas, or AI-generated outputs to train or fine-tune AI models. Your data
                                        is processed solely to provide you with the requested service and is not used to improve AI
                                        systems.
                                    </Text>
                                </div>
                                <Text variant="secondary" className="leading-relaxed mt-4">
                                    For more information, see{' '}
                                    <a
                                        href="https://www.anthropic.com/privacy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#7B61FF] hover:text-[#8A6DFF] underline"
                                    >
                                        Anthropic's Privacy Policy
                                    </a>.
                                </Text>
                            </section>

                            {/* Third-Party Services & Subprocessors */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">6. Third-Party Services & Subprocessors</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    We use the following third-party services (subprocessors) to provide our Service. Per GDPR
                                    Article 28, we have Data Processing Agreements (DPAs) in place with these providers:
                                </Text>
                                <div className="overflow-x-auto mb-4">
                                    <table className="w-full text-sm text-gray-300">
                                        <thead>
                                            <tr className="border-b border-white/10">
                                                <th className="text-left py-2 text-white">Provider</th>
                                                <th className="text-left py-2 text-white">Purpose</th>
                                                <th className="text-left py-2 text-white">Location</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/5">
                                            <tr>
                                                <td className="py-2">Google</td>
                                                <td className="py-2">Authentication (OAuth)</td>
                                                <td className="py-2">USA (EU SCCs)</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2">Anthropic</td>
                                                <td className="py-2">AI Processing (Claude API)</td>
                                                <td className="py-2">USA (EU SCCs)</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2">Paddle</td>
                                                <td className="py-2">Payment Processing (Merchant of Record)</td>
                                                <td className="py-2">UK/USA (EU SCCs)</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2">Cloudflare</td>
                                                <td className="py-2">Hosting, CDN, Security</td>
                                                <td className="py-2">Global (EU SCCs)</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <Text variant="secondary" className="leading-relaxed mb-2">
                                    <strong className="text-white">Privacy Policies:</strong>
                                </Text>
                                <ul className="list-disc list-inside space-y-1 text-gray-300">
                                    <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#7B61FF] hover:text-[#8A6DFF] underline">Google Privacy Policy</a></li>
                                    <li><a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#7B61FF] hover:text-[#8A6DFF] underline">Anthropic Privacy Policy</a></li>
                                    <li><a href="https://www.paddle.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-[#7B61FF] hover:text-[#8A6DFF] underline">Paddle Privacy Policy</a></li>
                                    <li><a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-[#7B61FF] hover:text-[#8A6DFF] underline">Cloudflare Privacy Policy</a></li>
                                </ul>
                            </section>

                            {/* Data Retention */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">7. Data Retention</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    We retain your data as follows:
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300">
                                    <li><strong className="text-white">Account data:</strong> Until you delete your account (soft delete with anonymization)</li>
                                    <li><strong className="text-white">Analysis sessions:</strong> Your analysis data (app ideas, AI-generated outputs) is retained
                                    while your account is active. This ensures you can access your previous analyses. Data is permanently deleted
                                    upon account deletion.</li>
                                    <li><strong className="text-white">Authentication sessions:</strong> Maximum 30 days with automatic token rotation every 24 hours.
                                    Sessions become inactive after 15 days without activity and require re-authentication.</li>
                                    <li><strong className="text-white">Payment records:</strong> Processed and retained by Paddle (our Merchant of Record) in accordance with legal
                                    requirements (typically 7 years). We store only subscription status and credit balances.</li>
                                    <li><strong className="text-white">Session cookies:</strong> Maximum 30 days from creation, extended on activity</li>
                                </ul>

                                <Text variant="secondary" className="leading-relaxed mt-6 mb-4">
                                    <strong className="text-white">Service Limits:</strong>
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300">
                                    <li><strong className="text-white">Workflow limits:</strong> Maximum 1 in-progress analysis workflow per user account</li>
                                    <li><strong className="text-white">Device limits:</strong> Maximum 5 devices per user account. New device registrations are
                                    rate-limited to prevent abuse.</li>
                                </ul>
                            </section>

                            {/* Your Rights - GDPR */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">8. Your Rights (GDPR - EU/UK Users)</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    If you are located in the European Union or United Kingdom, you have the following rights under
                                    EU GDPR and UK GDPR respectively:
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300">
                                    <li><strong className="text-white">Right to Access:</strong> Request a copy of your personal data</li>
                                    <li><strong className="text-white">Right to Rectification:</strong> Correct inaccurate personal data</li>
                                    <li><strong className="text-white">Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                                    <li><strong className="text-white">Right to Portability:</strong> Receive your data in a machine-readable format</li>
                                    <li><strong className="text-white">Right to Object:</strong> Object to processing based on legitimate interests</li>
                                    <li><strong className="text-white">Right to Restrict Processing:</strong> Limit how we use your data</li>
                                    <li><strong className="text-white">Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
                                    <li><strong className="text-white">Right to Lodge Complaint:</strong> File a complaint with your local supervisory authority</li>
                                </ul>
                                <Text variant="secondary" className="leading-relaxed mt-4">
                                    <strong className="text-white">Response Time:</strong> We will respond to your request within 30 days.
                                    To exercise these rights, please contact us through our{' '}
                                    <button onClick={() => navigate('/contact')} className="text-[#7B61FF] hover:text-[#8A6DFF] underline">
                                        contact page
                                    </button>.
                                </Text>
                            </section>

                            {/* Your Rights - CCPA/CPRA */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">9. Your Rights (CCPA/CPRA - California Users)</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    If you are a California resident, you have the following rights under CCPA and CPRA:
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300">
                                    <li><strong className="text-white">Right to Know:</strong> What personal information we collect and how it's used</li>
                                    <li><strong className="text-white">Right to Delete:</strong> Request deletion of your personal information</li>
                                    <li><strong className="text-white">Right to Correct:</strong> Request correction of inaccurate personal information (CPRA)</li>
                                    <li><strong className="text-white">Right to Opt-Out:</strong> Opt-out of the sale or sharing of personal information</li>
                                    <li><strong className="text-white">Right to Limit Sensitive Data:</strong> Limit the use of sensitive personal information (CPRA)</li>
                                    <li><strong className="text-white">Right to Non-Discrimination:</strong> We won't discriminate against you for exercising your rights</li>
                                </ul>
                                <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                                    <Text variant="secondary" className="leading-relaxed">
                                        <strong className="text-green-400">Do Not Sell or Share My Personal Information:</strong> We do not sell
                                        your personal information to third parties. We do not share your personal information for cross-context
                                        behavioral advertising. You have the right to opt-out of any future sale or sharing by contacting us.
                                    </Text>
                                </div>
                                <Text variant="secondary" className="leading-relaxed mt-4">
                                    <strong className="text-white">Response Time:</strong> We will respond to verified requests within 45 days,
                                    with a possible 45-day extension if needed.
                                </Text>
                            </section>

                            {/* Your Rights - LGPD Brazil */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">10. Your Rights (LGPD - Brazil Users)</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    If you are located in Brazil, you have the following rights under Lei Geral de Proteção de Dados (LGPD):
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300">
                                    <li><strong className="text-white">Confirmation & Access:</strong> Confirm whether we process your data and access it</li>
                                    <li><strong className="text-white">Correction:</strong> Correct incomplete or inaccurate data</li>
                                    <li><strong className="text-white">Anonymization/Blocking/Deletion:</strong> Request anonymization, blocking, or deletion of unnecessary data</li>
                                    <li><strong className="text-white">Portability:</strong> Request data portability to another service provider</li>
                                    <li><strong className="text-white">Information:</strong> Receive information about entities with whom we share your data</li>
                                    <li><strong className="text-white">Revocation:</strong> Revoke consent at any time</li>
                                </ul>
                            </section>

                            {/* Data Security */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">11. Data Security</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    We implement appropriate technical and organizational measures to protect your personal
                                    data, including encryption in transit (HTTPS), secure authentication (httpOnly cookies),
                                    and regular security assessments. However, no method of transmission over the Internet
                                    is 100% secure, and we cannot guarantee absolute security.
                                </Text>
                            </section>

                            {/* Children's Privacy */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">12. Children's Privacy</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    Our Service is not intended for users under 18 years of age. We do not knowingly collect
                                    personal information from children. If you believe we have collected information from a
                                    child, please contact us immediately.
                                </Text>
                            </section>

                            {/* International Transfers */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">13. International Data Transfers</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    Your information may be transferred to and processed in countries other than your country
                                    of residence. These countries may have different data protection laws. By using our Service,
                                    you consent to such transfers. We ensure appropriate safeguards are in place for international
                                    data transfers.
                                </Text>
                            </section>

                            {/* Changes to Privacy Policy */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">14. Changes to This Policy</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    We may update this Privacy Policy from time to time. We will notify you of any material
                                    changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
                                    We encourage you to review this Privacy Policy periodically.
                                </Text>
                            </section>

                            {/* Contact */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">15. Contact Us</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    If you have any questions about this Privacy Policy, our data practices, or wish to exercise
                                    your privacy rights, please contact us through our contact page:
                                </Text>
                                <Button
                                    onClick={() => navigate('/contact')}
                                    variant="ghost"
                                    className="text-[#7B61FF] hover:text-[#8A6DFF] underline p-0"
                                >
                                    Contact Us
                                </Button>
                            </section>

                            {/* Back Button */}
                            <div className="pt-8 border-t border-white/10 text-center">
                                <Button
                                    onClick={() => navigate(-1)}
                                    variant="ghost"
                                    className="flex items-center mx-auto"
                                >
                                    <Icon name="arrowLeft" size="xs" className="mr-2" />
                                    Go Back
                                </Button>
                            </div>
                        </div>
                    </Container>
                </main>

                <Footer />
            </div>
        </div>
    );
}
